import React, { useState, useEffect } from "react";
import { Card, Input, Button, Tooltip, Select } from "antd"; // Add Select to imports
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableAttackDefenceRow from "./SortableAttackDefenceRow";

const AttackDefenceManager = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAttackDefence, setSelectedAttackDefence] = useState(null);
  const [attackDefenceToDelete, setAttackDefenceToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    name: "",
    description: "",
  });

  // Pagination, search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // New state for status filter
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const debouncedStatusFilter = useDebounce(statusFilter, 500); // Debounce for status filter
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [attackDefenceList, setAttackDefenceList] = useState([]);

  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // Status filter options
  const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // API hooks
  const { execute: fetchList, loading: loadingList } = useGet(
    `${API_ENDPOINTS.ATTACK_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&status=${debouncedStatusFilter}`, // Added status to API call
  );
  const { post, loading: creating } = usePost(API_ENDPOINTS.ATTACK_LIST);
  const { execute: getById, loading: loadingById } = useDynamicApi("GET");
  const { execute: putData, loading: updating } = useDynamicApi("PUT");
  const { execute: patchData, loading: updatingStatus } =
    useDynamicApi("PATCH");
  const { execute: deleteData, loading: deleting } = useDynamicApi("DELETE");
  const { execute: postData, loading: posting } = useDynamicApi("POST");

  // Loading state
  const loading =
    loadingList ||
    loadingById ||
    updating ||
    updatingStatus ||
    deleting ||
    isReordering ||
    posting;

  // Load data with current pagination, search and filter
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        let data = response.data || [];
        // Sort by display_order when no search
        if (!searchTerm) {
          data = data.sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0),
          );
        }
        setAttackDefenceList(data);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const reorderItems = async (orderedIds) => {
    if (!orderedIds || orderedIds.length === 0) {
      Toast.error("No item IDs to reorder");
      return false;
    }
    try {
      setIsReordering(true);
      const response = await postData(API_ENDPOINTS.ATTACK_REORDER, {
        ordered_ids: orderedIds,
      });
      if (response?.status === true) {
        Toast.success(response?.message || "Items reordered successfully!");
        return true;
      }
      Toast.error(response?.message || "Failed to reorder items");
      return false;
    } catch (error) {
      Toast.error(error.message || "Failed to reorder items");
      return false;
    } finally {
      setIsReordering(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);
    if (searchTerm) return;
    if (!over || active.id === over.id) return;
    const oldIndex = attackDefenceList.findIndex((i) => i.id === active.id);
    const newIndex = attackDefenceList.findIndex((i) => i.id === over.id);
    if (oldIndex !== newIndex) {
      const newOrderedList = arrayMove(attackDefenceList, oldIndex, newIndex);
      setAttackDefenceList(newOrderedList);
      const success = await reorderItems(newOrderedList.map((i) => i.id));
      if (!success) setAttackDefenceList(attackDefenceList);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setIsDragging(false);
  };

  // Initial data fetch
  useEffect(() => {
    loadData();
  }, [currentPage, pageSize]);

  // Handle search and filter - triggers when debounced values change
  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm, debouncedStatusFilter]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      is_active: true,
    });
    setFormErrors({
      name: "",
      description: "",
    });
    setSelectedAttackDefence(null);
    setIsEditMode(false);
    setIsViewMode(false);
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === "") {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    } else if (formData.name.trim().length > 100) {
      errors.name = "Name must not exceed 100 characters";
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = "Description must not exceed 500 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create
  const handleCreate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      Toast.error("Please fill the details before submitting");
      return;
    }

    try {
      const response = await post(formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || "Attack/Defence created successfully!",
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        Toast.error(response?.message || "Failed to create Attack/Defence");
      }
    } catch (error) {
      console.error("Error creating Attack/Defence:", error);
      Toast.error(error.message || "Failed to create Attack/Defence");
    }
  };

  // Handle update
  const handleUpdate = async (e) => {
    e?.preventDefault?.();

    if (!validateForm()) {
      Toast.error("Please fix the form errors");
      return;
    }

    try {
      const endpoint = `${API_ENDPOINTS.ATTACK_UPDATE}/${selectedAttackDefence.id}`;
      const response = await putData(endpoint, formData);

      if (response?.status === true) {
        Toast.success(
          response?.message || "Attack/Defence updated successfully!",
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        Toast.error(response?.message || "Failed to update Attack/Defence");
      }
    } catch (error) {
      console.error("Error updating Attack/Defence:", error);
      Toast.error(error.message || "Failed to update Attack/Defence");
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (item) => {
    try {
      const newStatus = !item.is_active;
      const endpoint = `${API_ENDPOINTS.ATTACK_UPDATE}/${item.id}`;

      const response = await patchData(endpoint, {
        is_active: newStatus,
      });

      if (response?.status === true) {
        Toast.success(
          response?.message ||
            `Attack/Defence ${newStatus ? "activated" : "deactivated"} successfully!`,
        );
        await loadData();
      } else {
        Toast.error(response?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error(error.message || "Failed to update status");
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (item) => {
    setAttackDefenceToDelete(item);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!attackDefenceToDelete) return;

    try {
      const endpoint = `${API_ENDPOINTS.ATTACK_UPDATE}/${attackDefenceToDelete.id}`;
      const response = await deleteData(endpoint);

      if (response?.status === true) {
        Toast.success(
          response?.message || "Attack/Defence deleted successfully!",
        );
        await loadData();
      } else {
        Toast.error(response?.message || "Failed to delete Attack/Defence");
      }
    } catch (error) {
      console.error("Error deleting Attack/Defence:", error);
      Toast.error(error.message || "Failed to delete Attack/Defence");
    } finally {
      setIsDeleteModalOpen(false);
      setAttackDefenceToDelete(null);
    }
  };

  // Open modal for create
  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsViewMode(false);
    setIsModalOpen(true);
  };

  // Open modal for edit
  const openEditModal = async (item) => {
    try {
      const endpoint = `${API_ENDPOINTS.ATTACK_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedAttackDefence(response.data);
        setFormData({
          name: response.data.name,
          description: response.data.description,
          is_active: response.data.is_active,
        });
        setIsEditMode(true);
        setIsViewMode(false);
        setIsModalOpen(true);
      } else {
        Toast.error("Failed to fetch Attack/Defence details");
      }
    } catch (error) {
      console.error("Error fetching Attack/Defence details:", error);
      Toast.error(error.message || "Failed to fetch details");
    }
  };

  // Open modal for view
  const openViewModal = async (item) => {
    try {
      const endpoint = `${API_ENDPOINTS.ATTACK_UPDATE}/${item.id}`;
      const response = await getById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedAttackDefence(response.data);
        setFormData({
          name: response.data.name,
          description: response.data.description,
          is_active: response.data.is_active,
        });
        setIsEditMode(false);
        setIsViewMode(true);
        setIsModalOpen(true);
      } else {
        Toast.error("Failed to fetch Attack/Defence details");
      }
    } catch (error) {
      console.error("Error fetching Attack/Defence details:", error);
      Toast.error(error.message || "Failed to fetch details");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isDragEnabled = !searchTerm && !statusFilter && !loading; // Disable drag when filter is active too
  const activeItem = activeId
    ? attackDefenceList.find((i) => i.id === activeId)
    : null;

  const tableStyles = {
    td: {
      padding: "12px 16px",
      borderBottom: "1px solid #f0f0f0",
      fontSize: 14,
      color: "#333",
    },
    statusBadge: {
      padding: "4px 12px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 500,
      display: "inline-block",
    },
    statusActive: { backgroundColor: "#d4edda", color: "#155724" },
    statusInactive: { backgroundColor: "#f8d7da", color: "#721c24" },
    actionButtons: { display: "flex", gap: 8, flexWrap: "wrap" },
    actionButton: (type) => ({
      padding: "6px 12px",
      border: "none",
      borderRadius: 4,
      fontSize: 12,
      cursor: "pointer",
      fontWeight: 500,
      ...(type === "toggle"
        ? { backgroundColor: "#6c757d", color: "#fff" }
        : type === "view"
          ? { backgroundColor: "#17a2b8", color: "#fff" }
          : type === "edit"
            ? { backgroundColor: "#ffc107", color: "#212529" }
            : { backgroundColor: "#dc3545", color: "#fff" }),
    }),
  };

  return (
    <>
      <Card
        title="Attack/Defence Manager"
        extra={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!isDragEnabled && (searchTerm || statusFilter) && (
              <span
                style={{
                  fontSize: 13,
                  color: "#856404",
                  background: "#fff3cd",
                  padding: "6px 12px",
                  borderRadius: 4,
                }}
              >
                Clear search/filter to reorder
              </span>
            )}
            <Button type="primary" onClick={openCreateModal} disabled={loading}>
              + Create New Attack/Defence
            </Button>
          </span>
        }
      >
        <div style={{ display: "flex", gap: "16px", marginBottom: 16 }}>
          <Input
            placeholder="Search by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ maxWidth: 400 }}
            disabled={loading}
          />
          <Select
            placeholder="Filter by status"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            options={STATUS_OPTIONS}
            style={{ width: 200 }}
            allowClear
            disabled={loading}
          />
        </div>

        <div
          style={{
            position: "relative",
            background: "#fff",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {loadingList && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <span
                className="loader"
                style={{
                  border: "3px solid #f0f0f0",
                  borderTopColor: "#066744",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  animation: "spin 0.8s linear infinite",
                }}
              />
            </div>
          )}
          {isReordering && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9,
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  background: "#fff",
                  padding: "8px 16px",
                  borderRadius: 20,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                Saving order...
              </span>
            </div>
          )}

          <div className="table-scroll-wrap">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background: "#fafafa",
                    borderBottom: "2px solid #eee",
                  }}
                >
                  <th
                    style={{
                      width: 40,
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  />
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    Created At
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    Updated At
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loadingList && attackDefenceList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        padding: 40,
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      {searchTerm || statusFilter
                        ? "No matching entries found"
                        : "No Attack/Defence entries found"}
                    </td>
                  </tr>
                ) : (
                  <SortableContext
                    items={attackDefenceList.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                    disabled={!isDragEnabled}
                  >
                    {attackDefenceList.map((item) => (
                      <SortableAttackDefenceRow
                        key={item.id}
                        item={item}
                        loading={loading}
                        updatingStatus={updatingStatus}
                        searchTerm={searchTerm}
                        formatDate={formatDate}
                        handleToggleActive={handleToggleActive}
                        openViewModal={openViewModal}
                        openEditModal={openEditModal}
                        openDeleteModal={openDeleteModal}
                        isDragging={isDragging && activeId === item.id}
                        isDragEnabled={isDragEnabled}
                        styles={tableStyles}
                      />
                    ))}
                  </SortableContext>
                )}
              </tbody>
            </table>

            <DragOverlay>
              {activeItem && (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "#fff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <tbody>
                    <SortableAttackDefenceRow
                      item={activeItem}
                      loading={loading}
                      updatingStatus={updatingStatus}
                      searchTerm={searchTerm}
                      formatDate={formatDate}
                      handleToggleActive={handleToggleActive}
                      openViewModal={openViewModal}
                      openEditModal={openEditModal}
                      openDeleteModal={openDeleteModal}
                      isDragging
                      isOverlay
                      isDragEnabled={isDragEnabled}
                      styles={tableStyles}
                    />
                  </tbody>
                </table>
              )}
            </DragOverlay>
          </DndContext>
          </div>

          {!loadingList && attackDefenceList.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                padding: "16px 0",
                borderTop: "1px solid #eee",
                marginTop: 0,
              }}
            >
              <span style={{ fontSize: 14, color: "#666" }}>
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                entries
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button type="primary" disabled>
                  {currentPage}
                </Button>
                <Button
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </Button>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                  }}
                >
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit/View Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isViewMode
              ? "Attack/Defence Details"
              : isEditMode
                ? "Edit Attack/Defence"
                : "Create New Attack/Defence"
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? updating
                ? "Updating..."
                : "Update"
              : creating
                ? "Creating..."
                : "Create"
          }
          showConfirm={!isViewMode}
          confirmDisabled={creating || updating}
        >
          <form onSubmit={isEditMode ? handleUpdate : handleCreate}>
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter Attack/Defence name"
                required
                disabled={isViewMode || creating || updating}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              {formErrors.name && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.name}
                </div>
              )}
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "500",
                }}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description (optional)"
                disabled={isViewMode || creating || updating}
                rows="4"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              {formErrors.description && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {formErrors.description}
                </div>
              )}
            </div>

            {!isViewMode && (
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    disabled={creating || updating}
                  />
                  <span>Active</span>
                </label>
              </div>
            )}

            {isViewMode && selectedAttackDefence && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                }}
              >
                <div style={{ display: "flex", marginBottom: "8px" }}>
                  <strong style={{ width: "100px" }}>ID:</strong>
                  <span>{selectedAttackDefence.id}</span>
                </div>
                <div style={{ display: "flex", marginBottom: "8px" }}>
                  <strong style={{ width: "100px" }}>Display Order:</strong>
                  <span>{selectedAttackDefence.display_order || 0}</span>
                </div>
                <div style={{ display: "flex", marginBottom: "8px" }}>
                  <strong style={{ width: "100px" }}>Created At:</strong>
                  <span>{formatDate(selectedAttackDefence.created_at)}</span>
                </div>
                <div style={{ display: "flex", marginBottom: "8px" }}>
                  <strong style={{ width: "100px" }}>Updated At:</strong>
                  <span>{formatDate(selectedAttackDefence.updated_at)}</span>
                </div>
                <div style={{ display: "flex", marginBottom: "8px" }}>
                  <strong style={{ width: "100px" }}>Status:</strong>
                  <span
                    style={{
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "500",
                      display: "inline-block",
                      backgroundColor: selectedAttackDefence.is_active
                        ? "#d4edda"
                        : "#f8d7da",
                      color: selectedAttackDefence.is_active
                        ? "#155724"
                        : "#721c24",
                    }}
                  >
                    {selectedAttackDefence.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && attackDefenceToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setAttackDefenceToDelete(null);
          }}
          title="Confirm Delete"
          onConfirm={handleDeleteConfirm}
          confirmText={deleting ? "Deleting..." : "Delete"}
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>⚠️</div>
            <h3 style={{ marginBottom: "15px" }}>
              Are you sure you want to delete this Attack/Defence?
            </h3>
            <div
              style={{
                textAlign: "left",
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "6px",
                marginBottom: "15px",
              }}
            >
              <p>
                <strong>Name:</strong> {attackDefenceToDelete.name}
              </p>
              <p>
                <strong>Description:</strong>{" "}
                {attackDefenceToDelete.description || "N/A"}
              </p>
              <p>
                <strong>Display Order:</strong>{" "}
                {attackDefenceToDelete.display_order || 0}
              </p>
              <p>
                <strong>Created At:</strong>{" "}
                {formatDate(attackDefenceToDelete.created_at)}
              </p>
              <p>
                <strong>Status:</strong>
                <span
                  style={{
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "500",
                    display: "inline-block",
                    marginLeft: "10px",
                    backgroundColor: attackDefenceToDelete.is_active
                      ? "#d4edda"
                      : "#f8d7da",
                    color: attackDefenceToDelete.is_active
                      ? "#155724"
                      : "#721c24",
                  }}
                >
                  {attackDefenceToDelete.is_active ? "Active" : "Inactive"}
                </span>
              </p>
            </div>
            <p style={{ color: "#dc3545" }}>
              This action cannot be undone. All data related to this
              Attack/Defence will be permanently deleted.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AttackDefenceManager;
